import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/useAuthStore';
import { applySeo } from '@/functions/seo';
import { authReady } from '@/embedHost';

import Calendar from '@/pages/public/Calendar.vue';
import EventDetails from '@/pages/public/EventDetails.vue';
import EventRoster from '@/pages/public/EventRoster.vue';
import Services from '@/pages/public/Services.vue';
import Leaderboards from '@/pages/public/leaderboard/Leaderboards.vue';
import LeaderboardLigueHome from '@/pages/public/leaderboard/LeaderboardLigueHome.vue';
import LeaderboardScope from '@/pages/public/leaderboard/LeaderboardScope.vue';
import LeaderboardEvent from '@/pages/public/leaderboard/LeaderboardEvent.vue';
import LeaderboardEmbed from '@/pages/public/leaderboard/LeaderboardEmbed.vue';
import EventRegistrationForm from '@/pages/user/EventRegistrationForm.vue';
import EventInscriptions from '@/pages/user/EventInscriptions.vue';
import ClubSettings from '@/pages/user/ClubSettings.vue';
import ComiteSettings from '@/pages/user/ComiteSettings.vue';
import LigueSettings from '@/pages/user/LigueSettings.vue';
import ImportClassement from '@/pages/admin/ImportClassement.vue';
import BaremesManagement from '@/pages/admin/BaremesManagement.vue';
import AuthCallback from '@/pages/auth/AuthCallback.vue';
import Page404 from '@/pages/auth/Page404.vue';

const routes = [
  {
    path: '/', name: 'Calendar', component: Calendar,
    meta: {
      title: 'Calendrier des tournois de fléchettes',
      description: 'Tous les tournois de fléchettes au même endroit : opens de club, compétitions de comité, de ligue et nationales. Inscription et paiement en ligne.',
    },
  },
  { path: '/calendar', redirect: '/' },
  {
    path: '/services', name: 'Services', component: Services,
    meta: {
      title: 'Services et tarifs pour organisateurs de tournois',
      description: 'Inscriptions en ligne, paiement HelloAsso sans commission, pointage le jour J et export Darts4Windows, DartConnect, 3K Darts ou DartUP pour vos tournois de fléchettes.',
    },
  },
  { path: '/tarifs', redirect: { path: '/services', hash: '#tarifs' } },
  { path: '/evenement/:eventId', name: 'EventDetails', component: EventDetails },
  { path: '/evenement/:eventId/classement', name: 'LeaderboardEvent', component: LeaderboardEvent, meta: { wide: true } },
  {
    path: '/classement', name: 'Leaderboards', component: Leaderboards,
    meta: {
      title: 'Classements - Ligues et Comités',
      description: 'Retrouvez les classements officiels des Ligues et Comités de fléchettes, ainsi que le classement national FFD.',
    },
  },
  { path: '/classement/national', name: 'LeaderboardFederal', component: LeaderboardScope, meta: { wide: true } },
  { path: '/classement/:ligueCode', name: 'LeaderboardLigueHome', component: LeaderboardLigueHome },
  { path: '/classement/:ligueCode/ligue', name: 'LeaderboardLigue', component: LeaderboardScope, meta: { wide: true } },
  { path: '/classement/:ligueCode/:comiteCode', name: 'LeaderboardComite', component: LeaderboardScope, meta: { wide: true } },
  // Classements à intégrer en iframe sur un site tiers (cf. LeaderboardEmbed.vue) : sans
  // en-tête ni pied de page (meta.embed, cf. App.vue), non indexés (doublon de /classement).
  { path: '/embed/classement/national', name: 'EmbedLeaderboardFederal', component: LeaderboardEmbed, meta: { embed: true, noindex: true } },
  { path: '/embed/classement/:ligueCode/ligue', name: 'EmbedLeaderboardLigue', component: LeaderboardEmbed, meta: { embed: true, noindex: true } },
  { path: '/embed/classement/:ligueCode/:comiteCode', name: 'EmbedLeaderboardComite', component: LeaderboardEmbed, meta: { embed: true, noindex: true } },
  { path: '/inscription/:eventId', name: 'EventRegistrationForm', component: EventRegistrationForm, meta: { title: 'Inscription', noindex: true } },
  { path: '/event-roster/:eventId', name: 'EventRoster', component: EventRoster, meta: { title: 'Inscrits', noindex: true } },
  { path: '/event-inscriptions/:eventId', name: 'EventInscriptions', component: EventInscriptions, meta: { requiresAuth: true } },
  { path: '/mon-club', name: 'ClubSettings', component: ClubSettings, meta: { requiresAuth: true } },
  { path: '/mon-comite', name: 'ComiteSettings', component: ComiteSettings, meta: { requiresAuth: true } },
  { path: '/ma-ligue', name: 'LigueSettings', component: LigueSettings, meta: { requiresAuth: true } },
  { path: '/admin/import-classement', name: 'ImportClassement', component: ImportClassement, meta: { requiresAuth: true } },
  { path: '/admin/baremes', name: 'BaremesManagement', component: BaremesManagement, meta: { requiresAuth: true } },
  { path: '/auth/callback', name: 'AuthCallback', component: AuthCallback, meta: { noindex: true } },
  { path: '/:catchAll(.*)', name: 'Page404', component: Page404, meta: { title: 'Page introuvable', noindex: true } },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to) {
    if (to.hash) return { el: to.hash, behavior: 'smooth' };
    return { top: 0 };
  },
});

router.beforeEach(async (to) => {
  // Intégré par un site hôte : attendre sa session avant la première page (cf. embedHost.ts).
  await authReady;
  const authStore = useAuthStore();

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    authStore.openAuthModal();
    return { path: '/' };
  }
});

// Pages privées (requiresAuth) jamais indexées. La fiche événement ajuste ensuite ses
// propres balises une fois l'événement chargé (cf. EventDetails.vue).
router.afterEach((to) => {
  applySeo({
    title: to.meta.title as string | undefined,
    description: to.meta.description as string | undefined,
    path: to.path,
    noindex: Boolean(to.meta.noindex || to.meta.requiresAuth),
  });
});

export default router;
